import { ApolloError, gql, QueryResult } from '@apollo/client'
import * as React from 'react'
import { Link } from '../../../../common/src/components/Link'
import Table from '../../../../common/src/components/Table'
import TypeaheadBox from '../../../../common/src/components/TypeaheadBox'
import { Spacer, BodyText, IntroText, EllipsisDiv, BodyTextSpan, H2 } from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { addFeedback } from '../../../../common/src/feedback'
import memoize from '../../../../common/src/memoize'
import {
  FetchFeatureFlagsQueryVariables,
  FetchFeatureFlagsQuery,
  FeatureName,
  FetchFirmDetailsQuery,
} from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { PollingQuery } from '../components/PollingQuery'

const fetchFeatureFlagsQuery = gql`
  query FetchFeatureFlags($firmId: Int!) {
    featureFlags(firmId: $firmId) {
      featureName
      enabled
      description
    }
  }
`

const enableFeatureFlagMutation = gql`
  mutation EnableFeatureFlag($featureName: String!, $firmId: Int!, $firmTaskId: Int!) {
    enableFeatureFlag(featureName: $featureName, firmId: $firmId, firmTaskId: $firmTaskId) {
      featureName
      firmId
      enabled
    }
  }
`
const disableFeatureFlagMutation = gql`
  mutation DisableFeatureFlag($featureName: String!, $firmId: Int!, $firmTaskId: Int!) {
    disableFeatureFlag(featureName: $featureName, firmId: $firmId, firmTaskId: $firmTaskId) {
      featureName
      firmId
      enabled
    }
  }
`

const colWidths = ['160px', '500px', null]
const deprecatedFlags = new Set([
  FeatureName.REQUEST_MOVE_IN_DATE.toString(),
  FeatureName.VIRTUAL_TOUR_REMINDER_FOR_AGENTS.toString(),
  FeatureName.QUALIFY_MESSAGE_FOLLOWUP.toString(),
  FeatureName.RETRY_REACTIVATION.toString(),
  FeatureName.TRANSLATE_SPANISH_INBOUND.toString(),
  FeatureName.TRANSLATE_SPANISH_OUTBOUND.toString(),
  FeatureName.APM_IS_SOURCE_OF_TRUTH_FOR_POLICIES.toString(),
])
interface FeatureFlagsSectionProps {
  firm: FetchFirmDetailsQuery['firm']
}

class FeatureFlagsListImpl extends React.PureComponent<
  FeatureFlagsSectionProps & QueryResult<FetchFeatureFlagsQuery, FetchFeatureFlagsQueryVariables>
> {
  private handlers = {}

  render() {
    const { data, firm } = this.props
    if (data == null || data.featureFlags == null) {
      return null
    }
    let { featureFlags } = data
    featureFlags = featureFlags.filter(f => !deprecatedFlags.has(f.featureName.toString()))

    const cols = [
      (v: FetchFeatureFlagsQuery['featureFlags'][0]) => v.featureName,
      (v: FetchFeatureFlagsQuery['featureFlags'][0]) => v.description,
      (v: FetchFeatureFlagsQuery['featureFlags'][0]) => (
        <Link
          color="silver"
          onClick={memoize(this.handlers, v.featureName, () => () => this.removeFeatureFlagForFirm(v))}
        >
          Del
        </Link>
      ),
    ]

    const showClipboardLinks = featureFlags.length > 0

    return (
      <div>
        {showClipboardLinks && <Spacer height={4} />}
        {featureFlags.length > 0 && <Table rows={featureFlags} columns={cols} widths={colWidths} />}
        {featureFlags.length === 0 && <BodyText>No feature flags...</BodyText>}
        {<Spacer height={16} />}
        {<FeatureFlagTypeahead firmId={firm.id} firmTaskId={firm.firmTask!.id} featureFlags={featureFlags} />}
      </div>
    )
  }

  private removeFeatureFlagForFirm({ featureName }: FetchFeatureFlagsQuery['featureFlags'][0]) {
    if (!confirm(`Are you sure you want to remove feature flag ${featureName} for this firm?`)) {
      return
    }

    const { firm } = this.props
    const firmId = firm.id
    const firmTaskId = firm.firmTask!.id

    apolloWebClient
      .mutate({
        mutation: disableFeatureFlagMutation,
        variables: { featureName, firmId, firmTaskId },
      })
      .then(() => addFeedback('Removed feature flag'))
      .catch((err: ApolloError) => handleError(err))
  }
}

interface FeatureFlagResult {
  featureName: string
  description: string
}

class FeatureFlagTypeahead extends React.PureComponent<{
  firmId: number
  firmTaskId: number
  featureFlags: FetchFeatureFlagsQuery['featureFlags']
}> {
  render() {
    return (
      <TypeaheadBox<FeatureFlagResult>
        loadFunc={this.loadBound}
        rowFunc={FeatureFlagRow}
        onChoose={this.addFeatureFlagBound}
        placeholder="Add feature flag..."
        showResultsWhenEmpty
      />
    )
  }

  private loadBound = () => {
    const { featureFlags } = this.props

    const featureFlagNames = Object.keys(FeatureName).filter(f => !deprecatedFlags.has(f))
    const addableNames = featureFlagNames.filter(n => !featureFlags.find(f => f.featureName === n && f.enabled))

    const matches = addableNames.map(featureName => ({ featureName })) as FeatureFlagResult[]

    return Promise.resolve(matches)
  }

  private addFeatureFlagBound = (result: FeatureFlagResult) => {
    const { firmId, firmTaskId } = this.props
    const featureName = result.featureName
    apolloWebClient
      .mutate({
        mutation: enableFeatureFlagMutation,
        variables: { featureName, firmId, firmTaskId },
      })
      .then(() => addFeedback('Added feature flag'))
      .catch((err: ApolloError) => handleError(err))
  }
}

function FeatureFlagRow(props: FeatureFlagResult) {
  const { featureName, description } = props
  return (
    <IntroText>
      <EllipsisDiv>
        <BodyTextSpan>
          {featureName} &middot; {description}
        </BodyTextSpan>
      </EllipsisDiv>
    </IntroText>
  )
}

function FeatureFlagsList(props: FeatureFlagsSectionProps) {
  const variables: FetchFeatureFlagsQueryVariables = { firmId: props.firm.id }
  return (
    <PollingQuery query={fetchFeatureFlagsQuery} variables={variables}>
      {(res: QueryResult<FetchFeatureFlagsQuery, FetchFeatureFlagsQueryVariables>) => (
        <FeatureFlagsListImpl {...props} {...res} />
      )}
    </PollingQuery>
  )
}

export default class FeatureFlagsSection extends React.PureComponent<FeatureFlagsSectionProps> {
  render() {
    const { firm } = this.props

    return (
      <div>
        <H2>Feature Flags</H2>
        <Spacer height={8} />
        <BodyText>Add or remove feature flags for this firm.</BodyText>
        <Spacer height={8} />
        <FeatureFlagsList firm={firm} />
      </div>
    )
  }
}
